import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FileSpreadsheet, Wrench, Server, ShieldCheck } from 'lucide-react';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { FAQList } from '@/components/site/FAQ';
import { FaqSchema } from '@/components/site/Seo';
import { HERO_IMAGES } from '@/data/heroImages';
import { motionOn } from '@/lib/motion';
import {
  WRAP, SECTION, H2_SM, Accented, SectionHead,
  ServiceHero, ProblemsBlock, BuildBlock, ApproachBlock,
  OutcomesBlock, RelatedServices,
} from '@/components/site/ServicePageParts';

gsap.registerPlugin(ScrollTrigger);

const PROBLEMS = [
  { slug: 'spreadsheets', icon: FileSpreadsheet, title: 'Outgrowing spreadsheets', body: 'A 4,000-row Google Sheet that three people fight over isn’t a product—it’s a future incident report. We replace it with something that scales.' },
  { slug: 'clunky-tools', icon: Wrench, title: 'Clunky internal tools', body: 'Tools your team avoids cost twice—once in licence, once in the workarounds. We rebuild around the actual workflow, not the vendor’s.' },
  { slug: 'wont-scale', icon: Server, title: 'Systems that won’t scale', body: 'Apps that worked at 200 users break at 2,000. We architect for the next order of magnitude—queues, caching, read-replicas, the boring stuff.' },
  { slug: 'security', icon: ShieldCheck, title: 'Security and reliability', body: 'RBAC, audit logs, encryption, backups, uptime—we treat these as features, not afterthoughts. SOC 2 / ISO-ready by design.' },
];

const BUILD = [
  { slug: 'saas', tag: 'Multi-tenant · Billing', title: 'SaaS platforms', body: 'Multi-tenant apps with billing, plans, usage limits, admin and analytics—everything you need to charge from day one.', accent: '#6C5CE7' },
  { slug: 'admin', tag: 'Internal · Ops', title: 'Internal tools & admin dashboards', body: 'Replace the dozen spreadsheets and Retool patches with a single, opinionated console your team actually opens by default.', accent: '#4B9FFF' },
  { slug: 'portals', tag: 'B2B · Customer', title: 'Customer portals', body: 'Account, billing, support and self-service ops for B2B customers. Cut your support load in half.', accent: '#6C5CE7' },
  { slug: 'marketplaces', tag: 'Two-sided · Payments', title: 'Marketplaces', body: 'Two-sided platforms with onboarding, listings, search, escrow and payouts. Built for trust + take rate.', accent: '#4B9FFF' },
  { slug: 'realtime', tag: 'Sockets · Streams', title: 'Real-time apps', body: 'Collaborative tools, live dashboards and operations apps with websockets, presence and conflict resolution.', accent: '#4B9FFF' },
  { slug: 'apis', tag: 'Platforms · APIs', title: 'API products & platforms', body: 'Public or partner APIs with auth, rate-limits, docs, SDKs and metering. Your data, as a product.', accent: '#6C5CE7' },
];

const APPROACH = [
  { slug: 'discovery', step: '01', title: 'Discovery & architecture', body: 'Two weeks. Stakeholder interviews, data models, integration map, scaling plan. We leave with a one-page brief and a system diagram.' },
  { slug: 'ux-flows', step: '02', title: 'UX flows', body: 'Primary user journeys, edge cases, empty states, error states. Decisions made in low-fi so we argue about behaviour, not pixels.' },
  { slug: 'mvp', step: '03', title: 'MVP', body: 'A thin slice end-to-end—auth, one core flow, one core screen, observability. Real users on it in week 4–6.' },
  { slug: 'scalable-build', step: '04', title: 'Scalable build', body: 'The rest of the app, on the spine the MVP proved. Background workers, multi-tenant guards, caching, the right data store.' },
  { slug: 'qa', step: '05', title: 'QA', body: 'Unit + integration + a real human in a checklist. Performance budgets and a load test before launch—not after.' },
  { slug: 'deploy', step: '06', title: 'Deploy', body: 'Infra as code, blue/green, monitoring, alerting. The ops layer your platform engineer would have built—if you had one.' },
  { slug: 'iterate', step: '07', title: 'Iterate', body: 'A four-week hyper-care window post-launch, then a quarterly cadence. We stay through the second data point, not just the first.' },
];

const CAPS = [
  'Multi-tenant', 'Row-level security', 'RBAC', 'Audit logs', 'Webhooks',
  'GraphQL', 'REST', 'gRPC', 'WebSockets', 'Queues (SQS/Redis)', 'Workers',
  'Postgres', 'MongoDB', 'Redis', 'ClickHouse', 'S3 / R2', 'Kafka',
  'Auth0', 'Clerk', 'Cognito', 'Stripe', 'Razorpay', 'Sentry', 'PostHog',
];

const FAQ = [
  { q: 'Can you take over an existing codebase?', a: 'Yes, after a one-week audit. We will tell you what we are happy to inherit and what we recommend rewriting—with a rough cost and risk attached to each call.' },
  { q: 'What is your stance on no-code platforms?', a: 'They are great until they aren’t. We are happy to start on Retool / Bubble / Glide for v0 and migrate critical pieces to code as the product earns it.' },
  { q: 'Do you handle DevOps and infra?', a: 'Yes. We deploy to Vercel, Fly.io, Render, or your own AWS / GCP. We set up IaC (Terraform / SST), monitoring (Sentry, Grafana) and CI/CD as part of the build.' },
  { q: 'How do you keep our data safe?', a: 'Encrypted at rest and in transit by default. RBAC + audit trails on day one. SOC 2 / ISO-ready architecture. We sign mutual NDAs before the discovery call if needed.' },
  { q: 'What’s a typical timeline?', a: 'A focused v1 (MVP → public release) runs 10–16 weeks. A full platform with billing and multi-tenant ops runs 16–24 weeks.' },
  { q: 'Can you join an existing engineering team?', a: 'Yes. Our Partner engagement model embeds a small NioDelta cell inside your team—standups, retros, the works.' },
];

const OUTCOMES = [
  { kpi: 99.97, suffix: '%', label: 'uptime SLA achieved' },
  { kpi: 8, suffix: 'k', label: 'concurrent users / build' },
  { kpi: 62, prefix: '−', suffix: '%', label: 'support tickets after launch' },
  { kpi: 16, suffix: ' wks', label: 'avg. v1 to production' },
];

const ENGAGEMENT = [
  { title: 'MVP build', length: '6–10 weeks', price: '₹8–18L', body: 'One clear flow, end-to-end. Auth, core actions, lightweight analytics. The thinnest slice that earns the next investment.' },
  { title: 'Dedicated team', length: '3–6 months', price: '₹6L+ / mo', body: 'A fractional NioDelta cell—designer, two engineers, a producer—inside your roadmap.' },
  { title: 'Retainer', length: '6 months+', price: '₹3L+ / mo', body: 'Post-launch support, growth features, performance and security work on a steady cadence.' },
];

/* ----- Signature: Interactive dashboard that animates on scroll ----- */
const DASH_TARGETS = { revenue: 184230, mrr: 24800, churn: 2.4, signups: 612 };
const DASH_BARS = [22, 38, 30, 55, 47, 62, 70, 58, 76, 82, 70, 90];

function DashboardMockup() {
  const root = useRef(null);
  const numsRef = useRef({ ...DASH_TARGETS });
  // Final values by default, so reduced-motion and prerendered pages show a full console.
  const [nums, setNums] = useState(DASH_TARGETS);
  const barsRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    if (!motionOn() || !root.current) return undefined;
    const ctx = gsap.context(() => {
      const st = (end) => ({ trigger: root.current, start: 'top 75%', end, scrub: 0.6 });
      Object.assign(numsRef.current, { revenue: 0, mrr: 0, churn: 0, signups: 0 });
      gsap.to(numsRef.current, {
        ...DASH_TARGETS,
        ease: 'power2.out',
        scrollTrigger: st('bottom 30%'),
        onUpdate: () => setNums({ ...numsRef.current }),
      });
      // bars grow from the baseline (transform only)
      const bars = barsRef.current ? barsRef.current.querySelectorAll('[data-bar]') : [];
      gsap.fromTo(bars, { scaleY: 0.05 }, {
        scaleY: 1, ease: 'power2.out', stagger: 0.03, scrollTrigger: st('bottom 40%'),
      });
      // line stroke
      if (lineRef.current) {
        const path = lineRef.current;
        const len = path.getTotalLength();
        gsap.fromTo(path, { strokeDasharray: len, strokeDashoffset: len }, {
          strokeDashoffset: 0, ease: 'none', scrollTrigger: st('bottom 40%'),
        });
      }
    }, root);
    return () => ctx.revert();
  }, []);

  const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
  const label = 'font-mono text-[10px] uppercase tracking-[0.08em] text-white/50';
  const panel = 'rounded-[12px] border border-white/[0.08] bg-white/[0.02] p-4';

  return (
    <div
      ref={root}
      data-testid="wa-dashboard-mockup"
      className="relative w-full md:aspect-[16/10] rounded-[18px] overflow-hidden border border-white/[0.08] bg-ink-surface"
    >
      <div aria-hidden className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      {/* top bar */}
      <div className="relative h-12 border-b border-white/[0.08] px-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 font-display text-[13px] font-semibold tracking-[-0.02em]">
          <svg viewBox="0 0 32 32" className="h-4 w-4" aria-hidden><path d="M16 6 L26 24 L6 24 Z" fill="none" stroke="#6C5CE7" strokeWidth="2"/></svg>
          Kaveri<span className="text-white/50">Console</span>
        </div>
        <div className="hidden sm:flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.08em] text-white/40">
          <span>Overview</span><span className="text-white">Revenue</span><span>Customers</span><span>Settings</span>
          <span className="inline-flex items-center gap-2 text-white/70"><span className="nd-live" aria-hidden />Live</span>
        </div>
      </div>
      <div className="relative p-4 md:p-7 grid grid-cols-12 gap-3 md:gap-4 md:h-[calc(100%-3rem)]">
        {/* KPI tiles */}
        <div className="col-span-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { l: 'Revenue · MTD', v: inr(nums.revenue) },
            { l: 'MRR', v: inr(nums.mrr) },
            { l: 'Churn (rolling 30d)', v: `${nums.churn.toFixed(1)}%` },
            { l: 'Signups · 7d', v: Math.round(nums.signups).toString() },
          ].map((k) => (
            <div key={k.l} className={panel}>
              <div className={label}>{k.l}</div>
              <div className="mt-2 font-display font-medium text-xl md:text-[28px] leading-none tracking-[-0.04em] tabular-nums">
                {k.v}
              </div>
            </div>
          ))}
        </div>
        {/* Bars */}
        <div className={`col-span-12 md:col-span-7 flex flex-col ${panel}`}>
          <div className="flex items-center justify-between">
            <div className={label}>Revenue · last 12 weeks</div>
            <div className={label}>All amounts ₹</div>
          </div>
          <div ref={barsRef} className="mt-3 h-[120px] md:h-auto md:flex-1 flex items-end gap-1.5">
            {DASH_BARS.map((h, i) => (
              <div key={i} data-bar className="flex-1 rounded-[3px] origin-bottom"
                style={{
                  height: `${h}%`,
                  background: i === DASH_BARS.length - 1 ? 'var(--nd-grad)' : 'rgba(238,238,242,0.14)',
                }}
              />
            ))}
          </div>
        </div>
        {/* Line chart */}
        <div className={`col-span-12 md:col-span-5 ${panel}`}>
          <div className={label}>Active sessions · today</div>
          <svg viewBox="0 0 200 80" className="mt-3 w-full h-[120px]" aria-hidden>
            <defs>
              <linearGradient id="lineG" x1="0" x2="1">
                <stop offset="0%" stopColor="#6C5CE7" />
                <stop offset="100%" stopColor="#4B9FFF" />
              </linearGradient>
            </defs>
            <path
              ref={lineRef}
              d="M0,60 C20,55 30,40 45,38 C65,36 75,55 95,50 C115,46 130,18 150,22 C170,26 185,12 200,8"
              fill="none"
              stroke="url(#lineG)"
              strokeWidth="2"
            />
            {/* dots */}
            {[{x:45,y:38},{x:95,y:50},{x:150,y:22},{x:200,y:8}].map((d, i) => (
              <circle key={i} cx={d.x} cy={d.y} r="2.5" fill="#fff" />
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}

/* --- Capabilities marquee: oversized type, alternate words outlined --- */
function CapsMarquee({ items }) {
  const row = items.map((t, i) => (
    <span
      key={t}
      className={`inline-flex items-center gap-[clamp(18px,2.4vw,36px)] pr-[clamp(18px,2.4vw,36px)] font-display font-semibold text-[clamp(36px,5.4vw,84px)] tracking-[-0.045em] leading-[1.1] whitespace-nowrap ${i % 2 ? 'text-transparent [-webkit-text-stroke:1px_rgba(238,238,242,.32)]' : ''}`}
    >
      {t}
      <span aria-hidden className="h-[.16em] w-[.16em] rounded-full" style={{ background: 'var(--nd-grad)' }} />
    </span>
  ));
  return (
    <div className="relative border-y border-white/[0.08] py-[clamp(24px,4vw,48px)] overflow-hidden">
      <div aria-hidden className="absolute inset-y-0 left-0 w-[12vw] z-10 bg-gradient-to-r from-ink-bg to-transparent pointer-events-none" />
      <div aria-hidden className="absolute inset-y-0 right-0 w-[12vw] z-10 bg-gradient-to-l from-ink-bg to-transparent pointer-events-none" />
      <div className="flex w-max">
        <div className="flex items-center shrink-0 [animation:nd-mq_56s_linear_infinite]">{row}</div>
        <div className="flex items-center shrink-0 [animation:nd-mq_56s_linear_infinite]" aria-hidden>{row}</div>
      </div>
    </div>
  );
}

const PILLARS = [
  { k: 'Scalability', v: 'Designed for 10× your current load on day one. Read-replicas, queues and caching baked in.' },
  { k: 'Security', v: 'RBAC, audit logs, encryption, secrets, SAST in CI. SOC 2 / ISO-friendly architecture.' },
  { k: 'Integrations', v: 'Payments, CRMs, email/SMS, identity, accounting—the boring connectors that make a product useful.' },
];

export default function WebAppDevelopment() {
  return (
    <div data-testid="web-app-development-page">
      <FaqSchema items={FAQ} />
      <ServiceHero
        image={HERO_IMAGES['web-app-development']}
        serviceNum="02"
        serviceTitle="Web App Development"
        headlineText="Web applications that handle real work."
        accent="work"
        description="Multi-tenant SaaS, customer portals, marketplaces and dashboards engineered for the order of magnitude after the one you’re at. Boring infra, fast UI, observability you can sleep on."
        secondaryCta={{ label: 'See app work', to: '/work/' }}
        testIdPrefix="wa"
      />

      <ProblemsBlock
        heading="Four signs your app stack is the bottleneck."
        accentWord="bottleneck."
        intro="You don’t need a rewrite—you need someone to find the three things actually holding you back, fix them, and leave the rest alone."
        items={PROBLEMS}
        testIdPrefix="wa"
      />

      <BuildBlock
        heading="Six shapes of web app."
        accentWord="web app."
        intro="Every app is its own thing—but most live in one of these six shapes. We pick the right model in week one so the build matches the business."
        items={BUILD}
        testIdPrefix="wa"
      />

      <ApproachBlock
        heading="Seven stages. One file."
        accentWord="One file."
        intro="Designers and engineers in the same file from day one. Architecture before screens. Observability before launch."
        steps={APPROACH}
        testIdPrefix="wa"
      />

      {/* Capabilities marquee + emphasis row */}
      <section data-testid="wa-capabilities" className={`${SECTION} overflow-hidden`}>
        <div className={WRAP}>
          <SectionHead
            num="05"
            label="Capabilities"
            heading="A toolbox we’d defend in a system review."
            accent="defend"
            intro="We aren’t precious about tools—we are precious about what they do in production. Each pick earns its place."
          />
        </div>
        <CapsMarquee items={CAPS} />
        {/* Emphasis row */}
        <div className={`${WRAP} mt-16 md:mt-24`}>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-white/[0.08]">
            {PILLARS.map((e, i) => (
              <Reveal key={e.k} delay={i * 80} className={`pt-8 pb-2 md:pr-8 ${i ? 'max-md:mt-8 max-md:border-t md:pl-8 md:border-l' : ''} border-white/[0.08]`}>
                <div className="nd-mono">Pillar {String(i + 1).padStart(2, '0')}</div>
                <h3 className="mt-6 font-display font-semibold text-[clamp(28px,2.8vw,40px)] tracking-[-0.04em] leading-none">{e.k}</h3>
                <p className="mt-4 text-ink-muted leading-relaxed text-[15px] max-w-[40ch]">{e.v}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Signature — interactive dashboard mockup */}
      <section data-testid="wa-signature" className={SECTION}>
        <div className={WRAP}>
          <SectionHead
            num="06"
            label="Illustration · Live dashboard"
            heading="Demo console — scroll the page, watch the numbers move."
            accent="scroll the page,"
            intro="An illustrative operations console for a lender, not a client build: loan-book metrics, a disbursal heatmap and live collections on one screen."
          />
          <Reveal>
            <DashboardMockup />
          </Reveal>
        </div>
      </section>

      <OutcomesBlock
        heading="Numbers we’d sign a contract against."
        accentWord="contract"
        intro="Medians across the last 12 web-app engagements. Individual results vary—measurement doesn’t."
        values={OUTCOMES}
        testIdPrefix="wa"
      />

      {/* Engagement models */}
      <section data-testid="wa-engagements" className={SECTION}>
        <div className={WRAP}>
          <SectionHead num="08" label="Engagement models" heading="Three ways to work with us." accent="work with us." />
          <ul className="border-t border-white/[0.08]">
            {ENGAGEMENT.map((p, i) => (
              <Reveal as="li" key={p.title} delay={i * 60} className="border-b border-white/[0.08]">
                <div
                  data-testid={`wa-engagement-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="grid grid-cols-1 lg:grid-cols-[220px_1fr_auto] gap-x-8 gap-y-4 items-baseline py-9 md:py-12"
                >
                  <span className="nd-mono">{String(i + 1).padStart(2, '0')} · {p.length}</span>
                  <div>
                    <h3 className="font-display font-semibold text-[clamp(32px,4vw,64px)] tracking-[-0.045em] leading-none">{p.title}</h3>
                    <p className="mt-4 text-ink-muted leading-relaxed text-[15px] max-w-[56ch]">{p.body}</p>
                  </div>
                  <div className="font-display font-medium text-[clamp(28px,3vw,44px)] tracking-[-0.055em] leading-none tabular-nums lg:text-right whitespace-nowrap">{p.price}</div>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section data-testid="wa-faq" className={SECTION}>
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <SectionLabel num="09">FAQ</SectionLabel>
              <Reveal>
                <h2 className={`mt-7 ${H2_SM}`}><Accented text="Things we’re asked before the call." accent="before" /></h2>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-8">
            <FAQList items={FAQ} testIdPrefix="wa-faq" />
          </div>
        </div>
      </section>

      <RelatedServices exclude="web-app-development" testIdPrefix="wa" num="10" />

      <CTABand
        eyebrow="Ready for the next order of magnitude?"
        headline="Tell us what your app needs to do."
        sub="A 30-minute WhatsApp call with a partner. Bring the screenshot of the dashboard you wish you had."
      />
    </div>
  );
}

